import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTeamPortalAuth } from '../../contexts/TeamPortalAuthContext'
import { ArrowLeft, Clock, CheckCircle, XCircle, Trophy, Loader2 } from 'lucide-react'

const API_URL = process.env.REACT_APP_BACKEND_URL || ''

export default function MyJoinRequests() {
  const { token } = useTeamPortalAuth()
  const navigate = useNavigate()
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchRequests()
  }, [])

  const fetchRequests = async () => {
    try {
      const res = await fetch(`${API_URL}/api/invite-codes/my-requests`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Environment': 'production'
        }
      })

      if (res.ok) {
        const data = await res.json()
        setRequests(data)
      }
    } catch (err) {
      console.error('Error fetching requests:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-800">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        )
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-800">
            <CheckCircle className="w-3 h-3" />
            Approved
          </span>
        )
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-800">
            <XCircle className="w-3 h-3" />
            Rejected
          </span>
        )
      default:
        return (
          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-zinc-800 text-zinc-100">
            {status}
          </span>
        )
    }
  }

  return (
    <div className="min-h-screen bg-zinc-900 flex flex-col">
      {/* Header */}
      <div className="bg-zinc-800 border-b border-zinc-700">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          <button 
            onClick={() => navigate('/portal')}
            className="p-2 rounded-lg hover:bg-zinc-800 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 text-zinc-300" />
          </button>
          <h1 className="text-lg font-semibold text-white">My Join Requests</h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 py-6 max-w-lg mx-auto w-full">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12">
            <Trophy className="w-12 h-12 text-zinc-500 mx-auto mb-3" />
            <p className="text-zinc-400">No join requests yet</p>
            <button
              onClick={() => navigate('/portal/join')}
              className="mt-4 px-4 py-2 bg-black text-white text-sm font-medium rounded-lg hover:bg-zinc-700 transition-colors"
              data-testid="join-team-button"
            >
              Join a Team
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="requests-list">
            {requests.map((request) => (
              <div
                key={request.request_id}
                className="bg-zinc-800 rounded-xl border border-zinc-700 p-4"
                data-testid={`request-${request.request_id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {request.team_logo ? (
                      <img src={request.team_logo} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Trophy className="w-6 h-6 text-zinc-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white truncate">
                      {request.team_name}
                    </p>
                    <p className="text-sm text-zinc-400 capitalize">
                      Requested as: {request.role?.replace('_', ' ')}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">
                      {formatDate(request.requested_at)}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    {getStatusBadge(request.status)}
                  </div>
                </div>
                {request.message && (
                  <div className="mt-3 pt-3 border-t border-zinc-700">
                    <p className="text-sm text-zinc-300 italic">"{request.message}"</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Action */}
      {!loading && requests.length > 0 && (
        <div className="bg-zinc-800 border-t border-zinc-700 p-4">
          <div className="max-w-lg mx-auto">
            <button
              onClick={() => navigate('/portal/join')}
              className="w-full py-3 bg-black text-white font-medium rounded-xl hover:bg-zinc-700 transition-colors"
              data-testid="join-another-team-button"
            >
              Join Another Team
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
